import Time from '@/utils/Time';
import API from '@/services/api';
// import { Form } from '@CS/routes';
import { getUser } from '@/redux/user';
import FONTS from '@/assets/fonts.json';
import { getLogged } from '@/redux/auth';
import { User, MCD } from '@/services/app';
import CloseButton from '@/components/CloseButton';
import { ResponseError } from '@CS/components/Popups';
import { showConsole, isDEV } from '@/utils/Location';
// import { promptLogin, openHome } from '@/services/app/mcd';
import { Login, Home, Game /*, Quizzes */ } from '@/routes';
import type { ResponseErrorInfo } from '@/services/api/types';
import { Routes, Route, useLocation } from 'react-router-dom';
// import { withSentryReactRouterV6Routing } from '@sentry/react';
import { /* useRef, */ useState, useEffect, useCallback } from 'react';
import { useFonts, useLoader, useNetworkListener } from '@CS/hooks';
import TranslationProvider from '@CS/components/TranslationProvider';
import { useRecaptcha, useAppSelector, useNavigateQuery } from '@/hooks';

type ApplicationProps = {
  autoLogin?: boolean;
  login?: boolean;
};

export const Application = ({
  login = true,
  autoLogin = false
}: ApplicationProps) => {
  // const timeout = useRef(0);
  const { search } = useLocation();

  const navigate = useNavigateQuery();
  const fontsLoaded = useFonts(FONTS);

  const network = useNetworkListener();
  const { recaptcha } = useRecaptcha();
  const user = useAppSelector(getUser);

  const onLoad = () => setLoading(false);
  const logged = useAppSelector(getLogged);

  const [loading, setLoading] = useState(true);
  const cancelLogin = () => setLoginError(void 0);
  const { email, points, language, readonlyData } = user;

  const loader = useLoader(loading || !recaptcha || !fontsLoaded);
  const [loginError, setLoginError] = useState<ResponseErrorInfo>();

  const toggleFullscreen = () =>
    !isDEV() && ~MCD.toggleFullscreen() && MCD.orientationScreen();

  const getUserData = () =>
    Promise.all([User.checkUserPoints(), User.checkUserData()]);

  const onLogin = useCallback(async () => {
    toggleFullscreen();
    await getUserData();
    void navigate('/', 1);
    onLoad();
  }, [navigate]);

  const initApplication = useCallback(() => {
    const loginRedirect = () =>
      ~onLoad() && !autoLogin
        ? navigate('/login', -1)
        : MCD.promptLogin()
            .then(onLogin)
            .catch((error: ResponseErrorInfo | string | false) => {
              error = typeof error === 'string' && { message: error };
              setLoginError({ message: 'Prompt login error.', ...error });
            });

    const handleAuth = () =>
      (!email || typeof points !== 'number') &&
      getUserData().then(onLoad).catch(loginRedirect);

    ~API.revokeToken() && toggleFullscreen();
    login ? void handleAuth() : onLoad();

    Promise.all([User.checkUserCountry(), User.checkUserLanguage()])
      .then(([language]) => API.setCampaignData(language))
      .catch(async (error: Response) =>
        setLoginError(await API.getResponseErrorInfo(error))
      );
  }, [autoLogin, navigate, onLogin, email, points, login]);

  useEffect(() => {
    email &&
      !logged &&
      recaptcha &&
      API.login(user).catch(async (error: Response) =>
        setLoginError(await API.getResponseErrorInfo(error))
      );
  }, [email, logged, recaptcha, user]);

  useEffect(() => {
    /* const delayInitialization = () => {
      clearTimeout(timeout.current);

      if (!(window as Application).mcd && !isDEV()) {
        timeout.current = setTimeout(delayInitialization, 1e3);
      } else {
        !language && initApplication();
      }
    }; */

    const onVisibilityChange = () =>
      document.visibilityState === 'visible' && toggleFullscreen();

    ((window as Application).mcd || isDEV()) && !language && initApplication();
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('mcdBridgeReady', initApplication);
    // delayInitialization();

    return () => {
      document.removeEventListener('mcdBridgeReady', initApplication);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [language, initApplication]);

  useEffect(() => {
    showConsole((readonlyData as { console?: boolean })?.console);
  }, [readonlyData]);

  useEffect(() => {
    Time.setCurrentTime(search);
  }, [search]);

  /**
   * Sentry Tracing Routes Wrapper:
   *
  const RoutesWrapper = import.meta.env.VITE_SENTRY_DSN
    ? withSentryReactRouterV6Routing(Routes)
    : Routes; */

  return (
    loader ||
    network || (
      <TranslationProvider lang={language}>
        <CloseButton onClick={MCD.openHome} />

        <Routes>
          {/* <RoutesWrapper> */}
          {!autoLogin && (
            <Route path="/login" element={<Login onLogin={onLogin} />} />
          )}

          <Route path="/" element={<Home />} />
          <Route path="/game/:index?" element={<Game />} />
          {/* <Route path="/form" element={<Form user={user} />} /> */}
          {/* <Route path="/quizzes" element={<Quizzes />} /> */}
          {/* </RoutesWrapper> */}
        </Routes>

        {loginError && (
          <ResponseError info={loginError} onClose={cancelLogin} />
        )}
      </TranslationProvider>
    )
  );
};
